// External imports
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';

// Internal imports
import { Button } from '@/app/components/ui/button';
import { updateAnalyticsConsent } from '@/app/utils/analytics';

// Constants
const COOKIE_CONSENT_KEY = 'smartkubo_cookie_consent';

type ConsentStatus = 'pending' | 'accepted' | 'declined';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  preferences: boolean;
}

/**
 * CookieConsent - GDPR-compliant cookie consent banner
 * Displays on first visit and remembers user preference
 */
export function CookieConsent(): React.ReactElement | null {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>('pending');
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always required
    analytics: false,
    preferences: false,
  });

  // Check for existing consent on mount
  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      setConsentStatus(parsed.status);
      setPreferences(parsed.preferences);
      // Restore analytics consent state on page load
      updateAnalyticsConsent(parsed.preferences.analytics);
    } else {
      // Small delay before showing banner for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (status: ConsentStatus, prefs: CookiePreferences): void => {
    const consentData = {
      status,
      preferences: prefs,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));
    setConsentStatus(status);
    setShowBanner(false);
    
    // Update Google Analytics consent
    updateAnalyticsConsent(prefs.analytics);
  };

  const handleAcceptAll = (): void => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      preferences: true,
    };
    setPreferences(allAccepted);
    saveConsent('accepted', allAccepted);
  };

  const handleDeclineOptional = (): void => {
    const essentialOnly: CookiePreferences = {
      essential: true,
      analytics: false,
      preferences: false,
    };
    setPreferences(essentialOnly);
    saveConsent('declined', essentialOnly);
  };

  const handleSavePreferences = (): void => {
    saveConsent('accepted', preferences);
  };

  // Don't render if consent already given
  if (consentStatus !== 'pending' || !showBanner) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
      >
        <div className="max-w-4xl mx-auto bg-card rounded-2xl shadow-2xl border border-border overflow-hidden relative">
          {/* Close Button */}
          <button
            onClick={handleDeclineOptional}
            className="absolute top-4 right-4 p-2 text-foreground/40 hover:text-foreground transition-colors z-10"
            aria-label="Close cookie consent banner"
          >
            <X className="w-5 h-5" />
          </button>
          {/* Main Banner */}
          <div className="p-6 pr-12">
            <div className="flex items-start gap-4">
              <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-brand-marigold/20 flex-shrink-0">
                <Cookie className="w-6 h-6 text-brand-sienna" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h2 id="cookie-consent-title" className="text-lg font-semibold text-foreground mb-2">
                  We value your privacy
                </h2>
                <p id="cookie-consent-description" className="text-foreground/60 text-sm mb-4">
                  We use cookies to enhance your browsing experience, analyze site traffic, and 
                  personalize content. By clicking "Accept All", you consent to our use of cookies. 
                  Read our{' '}
                  <Link to="/privacy" className="text-brand-sienna hover:underline">
                    Privacy Policy
                  </Link>{' '}
                  for more information.
                </p>

                {/* Cookie Details (expandable) */}
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mb-4 p-4 bg-background/50 rounded-lg"
                  >
                    <h3 className="text-sm font-medium text-foreground mb-3">
                      Manage cookie preferences
                    </h3>
                    <div className="space-y-3">
                      {/* Essential Cookies */}
                      <label className="flex items-center gap-3 cursor-not-allowed">
                        <input
                          type="checkbox"
                          checked={preferences.essential}
                          disabled
                          className="w-4 h-4 rounded border-border"
                        />
                        <div>
                          <span className="text-sm font-medium text-foreground">
                            Essential Cookies
                          </span>
                          <span className="text-xs text-foreground/50 ml-2">(Required)</span>
                          <p className="text-xs text-foreground/50">
                            Necessary for the website to function properly
                          </p>
                        </div>
                      </label>
                      {/* Analytics Cookies */}
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.analytics}
                          onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                          className="w-4 h-4 rounded border-border text-brand-sienna focus:ring-brand-sienna"
                        />
                        <div>
                          <span className="text-sm font-medium text-foreground">
                            Analytics Cookies
                          </span>
                          <p className="text-xs text-foreground/50">
                            Help us understand how visitors interact with our website
                          </p>
                        </div>
                      </label>
                      {/* Preference Cookies */}
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.preferences}
                          onChange={(e) => setPreferences({ ...preferences, preferences: e.target.checked })}
                          className="w-4 h-4 rounded border-border text-brand-sienna focus:ring-brand-sienna"
                        />
                        <div>
                          <span className="text-sm font-medium text-foreground">
                            Preference Cookies
                          </span>
                          <p className="text-xs text-foreground/50">
                            Remember your settings and preferences
                          </p>
                        </div>
                      </label>
                    </div>
                  </motion.div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={handleAcceptAll}
                    className="bg-gradient-to-r from-brand-sienna to-brand-orange hover:from-brand-sienna/90 hover:to-brand-orange/90 text-white"
                  >
                    Accept All
                  </Button>
                  <Button
                    onClick={handleDeclineOptional}
                    variant="outline"
                    className="border-border text-foreground hover:bg-background"
                  >
                    Essential Only
                  </Button>
                  {showDetails ? (
                    <Button
                      onClick={handleSavePreferences}
                      variant="outline"
                      className="border-brand-sienna text-brand-sienna hover:bg-brand-sienna/10"
                    >
                      Save Preferences
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowDetails(true)}
                      variant="ghost"
                      className="text-foreground/60 hover:text-foreground"
                    >
                      Customize
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
